import React from 'react';
import { App, Button, Space, Table, Tag } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_HR_LETTER_REQUEST } from '@/constants/approval-entity-types';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor, showApprovalPanel } from './shared/hrmsStatus';

const LetterRequestsPage: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const { data: rows = [], isFetching } = useQuery({
    queryKey: hrmsKeys.letterRequests(),
    queryFn: () => hrmsService.listLetterRequests(),
  });
  const { data: detail } = useQuery({
    queryKey: hrmsKeys.letterRequest(selectedId || ''),
    queryFn: () => hrmsService.getLetterRequest(selectedId!),
    enabled: Boolean(selectedId),
  });

  const download = async (id: string, number: string) => {
    try {
      const blob = await hrmsService.downloadLetterRequestPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${number || 'letter'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Download failed');
    }
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Letter requests"
          subtitle="Approve and issue employee salary / experience / employment certificates"
        />
      }
    >
      <Table
        rowKey="id"
        loading={isFetching}
        dataSource={rows}
        onRow={(r) => ({ onClick: () => setSelectedId(r.id), style: { cursor: 'pointer' } })}
        columns={[
          { title: 'Number', dataIndex: 'request_number', width: 140 },
          { title: 'Type', dataIndex: 'letter_type' },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 140,
            render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
          },
          {
            title: 'Actions',
            width: 100,
            render: (_: unknown, r: any) =>
              r.status === 'Ready' ? (
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    download(r.id, r.request_number);
                  }}
                >
                  PDF
                </Button>
              ) : null,
          },
        ]}
      />
      {detail && showApprovalPanel(detail) && (
        <div style={{ marginTop: 16, maxWidth: 420 }}>
          <ApprovalPanel
            entityType={ENTITY_TYPE_HR_LETTER_REQUEST}
            entityId={detail.id}
            onApproveViaDocument={async (remarks) => {
              await hrmsService.approveLetterRequest(detail.id, remarks);
            }}
            onRejectViaDocument={async (payload) => {
              await hrmsService.rejectLetterRequest(detail.id, payload);
            }}
            extraInvalidateQueryKeys={[hrmsKeys.letterRequests(), hrmsKeys.letterRequest(detail.id)]}
          />
        </div>
      )}
      <Space style={{ marginTop: 16 }}>
        <Button onClick={() => navigate('/hr/me')}>Employee self-service</Button>
      </Space>
    </WorkspaceLayout>
  );
};

export default LetterRequestsPage;
